// Shared client helpers for the app.nz model directory. The catalogue is served
// live by the gateway (GET /api/gateway/models) from the same model_catalog.json
// the router uses, so the directory always matches what actually routes.

export interface CatalogModel {
  id: string;
  provider: string;
  aliases?: string[];
  fallback_models?: string[];
  input_price_per_1m?: number;
  output_price_per_1m?: number;
  context_window?: number;
  max_output_tokens?: number;
  modality?: string;
  // Media pricing the gateway exposes for non-text models.
  price_per_image?: number;
  price_per_video?: number;
  price_per_second?: number;
  price_per_minute?: number;
  price_per_forecast?: number;
  supported_sizes?: string[];
  supports_streaming: boolean;
  supports_tools: boolean;
  supports_vision: boolean;
}

export interface ProviderDetail {
  name: string;
  displayName: string;
  logo: string;
  website: string;
  modelCount: number;
}

export interface CatalogResponse {
  models: CatalogModel[];
  providerDetails: ProviderDetail[];
  total: number;
}

let cache: Promise<CatalogResponse> | null = null;

export function loadCatalog(): Promise<CatalogResponse> {
  if (!cache) {
    cache = fetch('/api/gateway/models')
      .then((r) => {
        if (!r.ok) throw new Error('catalogue unavailable');
        return r.json();
      })
      .catch((err) => {
        cache = null;
        throw err;
      });
  }
  return cache;
}

export function formatContext(tokens?: number): string {
  if (!tokens) return 'N/A';
  if (tokens >= 1_000_000) return `${+(tokens / 1_000_000).toFixed(tokens % 1_000_000 ? 1 : 0)}M`;
  if (tokens >= 1_000) return `${Math.round(tokens / 1_000)}K`;
  return String(tokens);
}

export function formatPrice(value?: number): string {
  if (value == null) return 'N/A';
  if (value === 0) return '$0';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  if (value < 1) return `$${value.toFixed(2)}`;
  return `$${value.toFixed(2)}`;
}

// Prettify a model id into a heading without losing accuracy: keep the id text
// but title-case the dash/slash segments for the eye.
export function modelTitle(id: string): string {
  const tail = id.includes('/') ? id.slice(id.indexOf('/') + 1) : id;
  return tail
    .split(/[-_]/)
    .map((w) => (/^\d/.test(w) || w.length <= 2 ? w : w[0].toUpperCase() + w.slice(1)))
    .join(' ');
}

export function modelDisplayLabel(id: string): string {
  const normalized = id.trim().toLowerCase();
  const tail = normalized.includes('/') ? normalized.slice(normalized.indexOf('/') + 1) : normalized;
  if (normalized === 'auto' || tail === 'auto' || tail === 'openpaths-auto') return 'Auto model';
  if (tail === 'auto-code') return 'Auto code';
  if (tail === 'auto-fast') return 'Auto fast';
  if (tail === 'auto-cheap') return 'Auto cheap';
  if (tail === 'auto-reasoning' || tail === 'auto-thinking' || tail === 'autothinking') return 'Auto thinking';
  if (tail === 'auto-vision') return 'Auto vision';
  if (tail === 'auto-image') return 'Auto image';
  return id;
}

export function capabilities(m: CatalogModel): string[] {
  const caps: string[] = [];
  if (m.supports_vision) caps.push('vision');
  if (m.supports_tools) caps.push('tools');
  if (m.supports_streaming) caps.push('streaming');
  return caps;
}
